import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import { Map } from 'immutable';
import FlowAlert from 'flow/components/custom-components/FlowAlert';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SubHeader from 'flow/components/Setting/SubHeader';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey, UserCredentialKey } from 'flow/types/EntityKeys';

import { routes } from 'flow/types/routes';
import Verified from './Verified';
import {
    addEmailAddress,
    getUserCredential,
} from 'flow/actions/user-credential-action';
import {
    canEditEmail,
    getVerifyingEmailCredential,
} from 'flow/utils/credential-utils';
import { generatePath } from 'flow/utils/router-utils';

const Email = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const { extensionKey, credentialId } = useParams<{
        extensionKey: ExtensionKey;
        credentialId: UserCredentialKey;
    }>();

    useEffect(() => {
        dispatch(getUserCredential(extensionKey));
    }, [dispatch, extensionKey]);

    const emailCredentials = useAppSelector((state) =>
        state.userCredentials
            .getIn([extensionKey, 'email'])
            ?.sort((a, b) =>
                a.get('credential') < b.get('credential') ? -1 : 1,
            ),
    );

    const successCredential = useAppSelector((state) =>
        state.userCredentials.get('successUserCredential'),
    );

    const credential = useAppSelector((state) =>
        state.userCredentials
            .getIn([extensionKey, 'email'], Map())
            .find((email) => email.get('key') === credentialId),
    );

    const validatingEmail = getVerifyingEmailCredential(emailCredentials);

    if (!credential) {
        return null;
    }
    return (
        <Page
            header={i18next.t('settings.user.credential.email_address')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.account.authentication'], {
                        extensionKey,
                    }),
                )
            }
        >
            <SubHeader>
                {i18next.t('settings.user.credential.email_address')}
            </SubHeader>

            <SettingRow
                cta={<Verified state={credential.get('state')} />}
                key={credential.get('key')}
            >
                {credential.get('credential')}
            </SettingRow>

            <Divider />

            {validatingEmail && (
                <Button
                    color="ghost"
                    className="mb-3"
                    onClick={() => {
                        dispatch(
                            addEmailAddress(extensionKey, validatingEmail),
                        );
                    }}
                >
                    {i18next.t(
                        'settings.user.credential.new_verification_mail',
                    )}
                </Button>
            )}

            <Button
                color="ghost"
                disabled={!canEditEmail(emailCredentials) && !successCredential}
                onClick={() =>
                    history.push(
                        generatePath(
                            routes[
                                'settings.account.username-password.email.register'
                            ],
                            { extensionKey },
                        ),
                    )
                }
            >
                {i18next.t('settings.user.credential.change_email')}
            </Button>

            {!canEditEmail(emailCredentials) && (
                <p className="mt-6 text-center text-xs text-gray-500">
                    {i18next.t(
                        'settings.user.credential.change_email_disabled',
                    )}
                </p>
            )}

            {successCredential && (
                <div className="mt-3">
                    <FlowAlert
                        header={i18next.t(
                            'settings.user.credential.titel_email_sent',
                        )}
                        text={i18next.t(
                            'settings.user.credential.text_email_sent',
                        )}
                    />
                </div>
            )}
        </Page>
    );
};

export default Email;
